import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";

import { createFaqVote } from "../../api/faq/createFaqVote";
import { getFaqVote } from "../../api/faq/getFaqVote";
import APImageWithLoader from "../../components/APImageWithLoader";
import { compileTemplate } from "../../helpers/faq";
import { parseHtmlWithClassComponentSubstitutions } from "../../helpers/html";
import { useAuth } from "../../providers/AuthProvider";
import { Dossier } from "../../types/Dossier";
import { APFaqVoteType, Faq } from "../../types/Faq";
import { QuoteFormatted } from "../../types/Quote";

const FaqDetails = ({
  faq,
  appLocationId,
  quote,
  dossier,
}: {
  faq: Faq;
  appLocationId: string | null;
  quote?: QuoteFormatted;
  dossier?: Dossier;
}) => {
  const faqVote = getFaqVote(faq.id, appLocationId);
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();

  const { mutateAsync, isLoading: isUpdating } = createFaqVote(faq.id);

  const handleVote = (voteType: APFaqVoteType | null) => {
    if (!isUpdating) {
      mutateAsync({
        vote: voteType,
        appLocationId,
        faqAppLocationId: faq.faqApplocationId,
      });
    }
  };

  const isUseful = faqVote.data?.vote === "useful";
  const isNotUseful = faqVote.data?.vote === "not-useful";

  const onNavigate = (path: string) => {
    navigate(path);
  };

  const compileAnswer = (answer: string) => {
    let compiledAnswer = answer;

    if (quote) {
      compiledAnswer = compileTemplate(compiledAnswer, quote, "quote");
    }

    if (dossier) {
      compiledAnswer = compileTemplate(compiledAnswer, dossier, "dossier");
    }

    return compiledAnswer;
  };

  return (
    <Stack spacing={2} sx={{ minWidth: "280px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="titleLarge">{faq.question}</Typography>
      </Box>
      <Box>
        <Typography variant="labelLarge">
          <Box className="faq-detail-container">
            {parseHtmlWithClassComponentSubstitutions(
              compileAnswer(faq.answer),
              {
                getScopedPageUrl,
                onNavigate,
                quote,
              }
            )}
          </Box>
        </Typography>
      </Box>
      <Box>
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "end" }}
        >
          <Typography variant="labelLarge" sx={{ mr: 1 }}>
            Useful?
          </Typography>
          <IconButton
            sx={{ color: "tertiaryGreen.main" }}
            onClick={() => handleVote(isUseful ? null : "useful")}
          >
            {isUseful ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
          </IconButton>

          <IconButton
            color="error"
            onClick={() => handleVote(isNotUseful ? null : "not-useful")}
          >
            {isNotUseful ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
          </IconButton>
        </Box>
      </Box>
      {faq.imageUrl && (
        <Box>
          <APImageWithLoader
            imageUrl={faq.imageUrl}
            width="100%"
            height="400px"
          />
        </Box>
      )}

      {faq.answerBelowImage && (
        <Box>
          <Typography variant="labelLarge">
            <Box className="faq-detail-container">
              {parseHtmlWithClassComponentSubstitutions(
                compileAnswer(faq.answerBelowImage),
                {
                  getScopedPageUrl,
                  onNavigate,
                  quote,
                }
              )}
            </Box>
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default FaqDetails;
